import { createApi } from "@reduxjs/toolkit/query/react";
import { components } from "../../types/api";
import { baseQueryWithReauth } from "../../utils/baseQuery";

type User = components["schemas"]["Omit_User.password_"] & {
  imageUrl?: string;
};
type Role = components["schemas"]["Role"];

type CreateUserPayload = {
  password: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    role?: Role;
  };
};

type CreateUserResponse =
  components["schemas"]["ApiResponse_Omit_User.password__"];
type GetUsersResponse =
  components["schemas"]["ApiResponse_Omit_User.password_-Array_"];
type UpdateUserResponse =
  components["schemas"]["ApiResponse_Omit_User.password__"];
type BulkDeleteResponse =
  components["schemas"]["ApiResponse_BulkDeleteResult_"];
type RecoverLinkResponse = components["schemas"]["ApiResponse_string_"];
type GetUserByIdResponse =
  components["schemas"]["ApiResponse_Omit_User.password__"];

export const usersApi = createApi({
  reducerPath: "usersApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Users"],
  endpoints: (builder) => ({
    getUserByEmail: builder.query<User, string>({
      query: (email) => `/users/by-email?email=${encodeURIComponent(email)}`,
      transformResponse: (response: { data: User }) => response.data,
    }),
    getUsers: builder.query<GetUsersResponse, void>({
      query: () => "/users",
      providesTags: ["Users"],
      transformResponse: (response: GetUsersResponse) => response,
    }),
    deleteUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `/users/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Users"],
    }),
    updateUser: builder.mutation<
      UpdateUserResponse,
      { id: string; updates: Partial<User> }
    >({
      query: ({ id, updates }) => ({
        url: `/users/${id}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: ["Users"],
    }),
    bulkDeleteUsers: builder.mutation<BulkDeleteResponse, string[]>({
      query: (emails) => ({
        url: "/users/bulk",
        method: "DELETE",
        body: { emails },
      }),
      invalidatesTags: ["Users"],
    }),
    recoverLink: builder.mutation<RecoverLinkResponse, string>({
      query: (email) => ({
        url: "/users/recover-password",
        method: "POST",
        body: { email },
      }),
    }),
    createUser: builder.mutation<CreateUserResponse, CreateUserPayload>({
      query: (newUser) => ({
        url: "/users",
        method: "POST",
        body: newUser,
      }),
      invalidatesTags: ["Users"],
    }),
    getUserById: builder.query<GetUserByIdResponse, string>({
      query: (id) => `/users/${id}`,
    }),
  }),
});

export const {
  useGetUsersQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useBulkDeleteUsersMutation,
  useRecoverLinkMutation,
  useGetUserByEmailQuery,
  useGetUserByIdQuery,
} = usersApi;
